// coloringProjectsService.js
// Toutes les operations Supabase pour le Carnet de couleurs.
// Importez ce service dans les composants React — jamais le client Supabase directement.

import { supabase } from '../lib/supabase';
import { KOFI_CONFIG } from '../config/kofi';

// ---- Constantes ----
const TABLE_PROJECTS = 'coloring_projects';
const TABLE_MARKERS  = 'project_markers';
const BUCKET_PHOTOS  = 'coloring-project-photos';

// Quota actif par statut contributeur
export function getActiveProjectQuota(isContributor) {
  return isContributor
    ? KOFI_CONFIG.coloringProjectLimits.contributor
    : KOFI_CONFIG.coloringProjectLimits.free;
}

// ============================================================
// PROJETS
// ============================================================

/**
 * Compte les projets ACTIFS de l'utilisateur courant.
 * Retourne un entier. Lance une erreur si la requete echoue.
 */
export async function countActiveProjects() {
  const { count, error } = await supabase
    .from(TABLE_PROJECTS)
    .select('id', { count: 'exact', head: true })
    .eq('status', 'active');

  if (error) throw error;
  return count ?? 0;
}

/**
 * Liste les projets actifs, tries par updated_at DESC, limites a `limit` resultats.
 * Inclut le compte de marqueurs via une aggregation cote client apres un second fetch.
 * Retourne un tableau d'objets { ...projet, markerCount }.
 */
export async function listActiveProjects(limit = 10) {
  const { data, error } = await supabase
    .from(TABLE_PROJECTS)
    .select('*')
    .eq('status', 'active')
    .order('updated_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  if (!data || data.length === 0) return [];

  // Charger les counts de marqueurs pour chaque projet
  const projectIds = data.map((p) => p.id);
  const { data: markerCounts, error: mcError } = await supabase
    .from(TABLE_MARKERS)
    .select('project_id')
    .in('project_id', projectIds);

  if (mcError) throw mcError;

  const countMap = {};
  (markerCounts || []).forEach((m) => {
    countMap[m.project_id] = (countMap[m.project_id] || 0) + 1;
  });

  return data.map((p) => ({ ...p, markerCount: countMap[p.id] ?? 0 }));
}

/**
 * Liste TOUS les projets (actifs + termines), tries par updated_at DESC.
 * Utilisee par la page "Mes coloriages" dans le Studio.
 */
export async function listAllProjects() {
  const { data, error } = await supabase
    .from(TABLE_PROJECTS)
    .select('*')
    .order('updated_at', { ascending: false });

  if (error) throw error;
  if (!data || data.length === 0) return [];

  const projectIds = data.map((p) => p.id);
  const { data: markerCounts, error: mcError } = await supabase
    .from(TABLE_MARKERS)
    .select('project_id')
    .in('project_id', projectIds);

  if (mcError) throw mcError;

  const countMap = {};
  (markerCounts || []).forEach((m) => {
    countMap[m.project_id] = (countMap[m.project_id] || 0) + 1;
  });

  return data.map((p) => ({ ...p, markerCount: countMap[p.id] ?? 0 }));
}

/**
 * Charge un projet par son ID (avec ses marqueurs).
 * Retourne { project, markers } ou null si non trouve.
 */
export async function getProject(projectId) {
  const { data: project, error: pError } = await supabase
    .from(TABLE_PROJECTS)
    .select('*')
    .eq('id', projectId)
    .single();

  if (pError) {
    if (pError.code === 'PGRST116') return null; // not found
    throw pError;
  }

  const { data: markers, error: mError } = await supabase
    .from(TABLE_MARKERS)
    .select('*')
    .eq('project_id', projectId)
    .order('added_at', { ascending: true });

  if (mError) throw mError;

  return { project, markers: markers || [] };
}

/**
 * Cree un nouveau projet.
 * Verifie le quota AVANT l'insertion.
 * Retourne { project } ou lance une Error avec err.type === 'QUOTA_EXCEEDED'.
 */
export async function createProject({ title, photoUrl = null, generalNotes = null, isContributor = false }) {
  const quota = getActiveProjectQuota(isContributor);
  const activeCount = await countActiveProjects();

  if (activeCount >= quota) {
    const err = new Error('Quota de projets actifs atteint');
    err.type = 'QUOTA_EXCEEDED';
    err.quota = quota;
    err.activeCount = activeCount;
    throw err;
  }

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Non connecte');

  const { data, error } = await supabase
    .from(TABLE_PROJECTS)
    .insert({
      user_id: user.id,
      title: title.trim(),
      photo_url: photoUrl,
      general_notes: generalNotes,
      status: 'active',
    })
    .select()
    .single();

  if (error) throw error;
  return { project: data };
}

/**
 * Met a jour les champs editables d'un projet (titre, photo_url, general_notes).
 * Retourne le projet mis a jour.
 */
export async function updateProject(projectId, { title, photoUrl, generalNotes }) {
  const updates = {};
  if (title !== undefined) updates.title = title.trim();
  if (photoUrl !== undefined) updates.photo_url = photoUrl;
  if (generalNotes !== undefined) updates.general_notes = generalNotes;

  const { data, error } = await supabase
    .from(TABLE_PROJECTS)
    .update(updates)
    .eq('id', projectId)
    .select()
    .single();

  if (error) throw error;
  return { project: data };
}

/**
 * Passe un projet en statut 'done'.
 * Libere immediatement une place de quota.
 */
export async function finishProject(projectId) {
  const { data, error } = await supabase
    .from(TABLE_PROJECTS)
    .update({ status: 'done' })
    .eq('id', projectId)
    .select()
    .single();

  if (error) throw error;
  return { project: data };
}

/**
 * Supprime definitivement un projet et tous ses marqueurs (cascade SQL).
 * Libere immediatement une place de quota.
 */
export async function deleteProject(projectId) {
  const { error } = await supabase
    .from(TABLE_PROJECTS)
    .delete()
    .eq('id', projectId);

  if (error) throw error;
  return { success: true };
}

// ============================================================
// MARQUEURS
// ============================================================

/**
 * Ajoute un marqueur a un projet.
 * Pour marker_type='ohuhu' : fournir ohuhuCode + ohuhuHex.
 * Pour marker_type='manual' : fournir manualBrand + manualCode + manualHex (tous optionnels sauf au moins un champ non vide).
 * label, zone, applicationOrder sont toujours optionnels.
 */
export async function addMarker(projectId, {
  markerType,
  ohuhuCode = null,
  ohuhuHex = null,
  manualBrand = null,
  manualCode = null,
  manualHex = null,
  label = null,
  zone = null,
  applicationOrder = null,
}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Non connecte');

  const { data, error } = await supabase
    .from(TABLE_MARKERS)
    .insert({
      project_id: projectId,
      user_id: user.id,
      marker_type: markerType,
      ohuhu_code: ohuhuCode,
      ohuhu_hex: ohuhuHex,
      manual_brand: manualBrand,
      manual_code: manualCode,
      manual_hex: manualHex,
      label: label ? label.trim() : null,
      zone: zone ? zone.trim() : null,
      application_order: applicationOrder ? parseInt(applicationOrder, 10) : null,
    })
    .select()
    .single();

  if (error) throw error;
  return { marker: data };
}

/**
 * Met a jour un marqueur existant.
 * Seuls les champs fournis sont mis a jour.
 */
export async function updateMarker(markerId, fields) {
  const updates = {};
  if (fields.label !== undefined)            updates.label = fields.label ? fields.label.trim() : null;
  if (fields.zone !== undefined)             updates.zone = fields.zone ? fields.zone.trim() : null;
  if (fields.applicationOrder !== undefined) updates.application_order = fields.applicationOrder ? parseInt(fields.applicationOrder, 10) : null;
  if (fields.manualBrand !== undefined)      updates.manual_brand = fields.manualBrand;
  if (fields.manualCode !== undefined)       updates.manual_code = fields.manualCode;
  if (fields.manualHex !== undefined)        updates.manual_hex = fields.manualHex;

  const { data, error } = await supabase
    .from(TABLE_MARKERS)
    .update(updates)
    .eq('id', markerId)
    .select()
    .single();

  if (error) throw error;
  return { marker: data };
}

/**
 * Supprime un marqueur.
 */
export async function deleteMarker(markerId) {
  const { error } = await supabase
    .from(TABLE_MARKERS)
    .delete()
    .eq('id', markerId);

  if (error) throw error;
  return { success: true };
}

// ============================================================
// STORAGE — PHOTOS
// ============================================================

/**
 * Compresse une image cote client (canvas) et l'uploade dans le bucket.
 * Retourne l'URL signed (1 an).
 * Leve une erreur si l'upload echoue.
 *
 * @param {File} file - Fichier image original
 * @param {string} projectId - ID du projet (pour le chemin de stockage)
 * @returns {Promise<string>} URL Supabase Storage signed (1 an)
 */
export async function uploadProjectPhoto(file, projectId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Non connecte');

  // Compression cote client : redimensionne a max 800px, encode en JPEG qualite 85
  const compressed = await compressImage(file, 800, 0.85);

  const ext = 'jpg';
  const filename = `${user.id}/${projectId}/${Date.now()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET_PHOTOS)
    .upload(filename, compressed, {
      contentType: 'image/jpeg',
      upsert: true,
    });

  if (uploadError) throw uploadError;

  const { data: signedUrlData, error: signedUrlError } = await supabase.storage
    .from(BUCKET_PHOTOS)
    .createSignedUrl(filename, 60 * 60 * 24 * 365); // 1 an

  if (signedUrlError) throw signedUrlError;
  return signedUrlData.signedUrl;
}

/**
 * Comprime une image via un canvas HTML.
 * @param {File} file
 * @param {number} maxDim - Dimension max (largeur ou hauteur)
 * @param {number} quality - Qualite JPEG 0-1
 * @returns {Promise<Blob>}
 */
function compressImage(file, maxDim, quality) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      let { width, height } = img;

      if (width > maxDim || height > maxDim) {
        if (width > height) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Echec de la compression de l\'image'));
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Impossible de charger l\'image'));
    };

    img.src = url;
  });
}

/**
 * Supprime la photo d'un projet du bucket Storage.
 * photoStoragePath doit etre le chemin interne au bucket (ex : "uid/projectId/123.jpg").
 * Silencieux si la photo n'existe pas.
 */
export async function deleteProjectPhoto(photoStoragePath) {
  if (!photoStoragePath) return;

  const { error } = await supabase.storage
    .from(BUCKET_PHOTOS)
    .remove([photoStoragePath]);

  // On ignore les erreurs 404 (fichier deja supprime)
  if (error && !error.message.includes('Not Found')) throw error;
}

// ============================================================
// HOOK POST-SCAN : creation rapide depuis resultats Scan
// ============================================================

/**
 * Cree un projet a partir des resultats d'un scan Ohuhu.
 * Pre-remplit les marqueurs Ohuhu identifies par le scan.
 * Retourne { project, markers, quotaExceeded: false } ou { quotaExceeded: true, quota, activeCount }.
 *
 * @param {string} title - Nom du projet
 * @param {Array<{code: string, hex: string}>} ohuhuMatches - Marqueurs Ohuhu identifies par le scan
 * @param {boolean} isContributor
 */
export async function createProjectFromScan(title, ohuhuMatches, isContributor) {
  let created;
  try {
    created = await createProject({ title, isContributor });
  } catch (err) {
    if (err.type === 'QUOTA_EXCEEDED') {
      return { quotaExceeded: true, quota: err.quota, activeCount: err.activeCount };
    }
    throw err;
  }

  const projectId = created.project.id;
  const addedMarkers = [];

  for (const match of ohuhuMatches) {
    // eslint-disable-next-line no-await-in-loop
    const { marker } = await addMarker(projectId, {
      markerType: 'ohuhu',
      ohuhuCode: match.code,
      ohuhuHex: match.hex,
    });
    addedMarkers.push(marker);
  }

  return { quotaExceeded: false, project: created.project, markers: addedMarkers };
}
